import { useTranslation } from 'react-i18next';

import { captureException } from '@sentry/browser';

import { PageInfo } from '../../../types/__generated__';
import { getBackendErrorMessage } from '../../utils/getBackendErrorMessage';
import { useApp } from '../index';
import { useCreateTimelineEntry } from './useCreateTimelineEntry';
import { useDeleteTimelineEntry } from './useDeleteTimelineEntry';
import { useListTimelineEntries } from './useListTimelineEntries';
import { useRefreshTimelineEntry } from './useRefreshTimelineEntry';

export function useTimeline() {
  const { t } = useTranslation('common', { useSuspense: false });
  const { showToast } = useApp();

  const { loading, data, fetchMore } = useListTimelineEntries();
  const [createTimelineEntryMutation] = useCreateTimelineEntry();
  const [refreshTimelineEntryMutation] = useRefreshTimelineEntry();
  const [deleteTimelineEntryMutation] = useDeleteTimelineEntry();

  const createTimelineEntry = async (variables: any) => {
    try {
      await createTimelineEntryMutation(variables);

      showToast({
        message: t('timeline.createTimelineEntry.confirmationMessage'),
        type: 'success',
        dismissAfter: 10000,
      });
    } catch (err) {
      showToast({
        message: t('timeline.createTimelineEntry.errorMessage'),
        type: 'warning',
        dismissAfter: 10000,
      });

      captureException(err);

      // Caught by formik so we can update the form
      throw err;
    }
  };

  const fetchNextPage = async (after: string) => {
    try {
      await fetchMore({
        variables: { after },
      });
    } catch (err) {
      showToast({
        message: t('timeline.fetchNextPage.errorMessage'),
        type: 'warning',
        dismissAfter: 10000,
      });

      captureException(err);
    }
  };

  const deleteTimelineEntry = async (id: string, pageInfo?: PageInfo) => {
    try {
      await deleteTimelineEntryMutation(id, pageInfo);

      showToast({
        message: t('timeline.deleteTimelineEntry.confirmationMessage'),
        type: 'success',
        dismissAfter: 10000,
      });
    } catch (err) {
      showToast({
        message: t('timeline.deleteTimelineEntry.errorMessage'),
        type: 'warning',
        dismissAfter: 10000,
      });

      captureException(err);
    }
  };

  const refreshTimelineEntry = async (id: string, onSuccess: () => void) => {
    try {
      await refreshTimelineEntryMutation(id);

      showToast({
        type: 'success',
        message: t('timeline.refreshTimelineEntryRequested'),
        dismissAfter: 5000,
      });

      onSuccess();
    } catch (err) {
      showToast({
        type: 'warning',
        message: getBackendErrorMessage(err),
        dismissAfter: 10000,
      });

      captureException(err);
    }
  };

  return {
    timeline: data?.page?.timeline,
    fetchMoreLoading: loading,
    createTimelineEntry,
    deleteTimelineEntry,
    refreshTimelineEntry,
    fetchNextPage,
  };
}
